import type { ReactiveController, ReactiveControllerHost } from 'lit'
import type {
  Translation,
  WellKnownKeys,
} from './translation.js'
import { isServer } from 'lit'
import {
  fallback,
  resolveTranslation,
  subscribeToDocumentLang,
  subscribeToTranslationRegistration,
} from './translation.js'

// Use fallback (en) as source of truth for valid translation keys
type FallbackType = typeof fallback

// helper types...

// used to match any function
type Func = (...args: any[]) => any

// if object property is function, use return type, else value type
type Result<Type, Key extends keyof Type> = Type[Key] extends Func ? ReturnType<Type[Key]> : Type[Key]

// if object property is function,
type FuncParams<Type, K extends keyof Type> = Type[K] extends Func ? Parameters<Type[K]> : []

function noop() {
  // this function is intentionally left blank
}

export class LocalizeController<TComponentName extends Exclude<keyof Translation, WellKnownKeys>>
implements ReactiveController {
  private unsubscribeDocumentLang?: ReturnType<typeof subscribeToDocumentLang>
  private unsubscribeTranslationRegistration?: ReturnType<typeof subscribeToTranslationRegistration>
  private observer?: MutationObserver
  private resolvedTranslation: Translation

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options = { onLangChange: noop },
  ) {
    host.addController(this)
    this.resolvedTranslation = resolveTranslation(this.lang)
  }

  /**
   * The lang of the document or element, with element taking precedence
   */
  get lang() {
    if (isServer) {
      return this.host.lang || 'en-US'
    }

    return this.host.lang || document.documentElement.lang
  }

  /**
   * The lang of the translation being applied.
   * This may not match the document/element lang, in case of fallback translation
   */
  get resolvedLang() {
    return this.resolvedTranslation.$lang
  }

  hostConnected() {
    // Always subscribe to translation registration changes (for when new translations are registered)
    this.unsubscribeTranslationRegistration = subscribeToTranslationRegistration(this.handleLangChange)

    // Re-resolve translation now that the host is connected and attributes are set
    // This handles cases where the lang attribute is set after the constructor runs (e.g., in Vue)
    const resolved = resolveTranslation(this.lang)
    if (this.resolvedTranslation !== resolved) {
      this.resolvedTranslation = resolved
    }

    // If the host has its own `lang` attribute, create a local observer to watch for changes to that attribute
    if (this.host.getAttribute('lang')) {
      this.observer = new MutationObserver(this.handleLangChange)
      this.observer.observe(this.host, {
        attributes: true,
        attributeFilter: ['lang'],
      })
    }
    else {
      // Otherwise, also subscribe to document language changes
      this.unsubscribeDocumentLang = subscribeToDocumentLang(this.handleLangChange)
    }

    this.options.onLangChange()
  }

  hostDisconnected() {
    this.unsubscribeDocumentLang?.()
    this.unsubscribeTranslationRegistration?.()
    this.observer?.disconnect()
  }

  term<Key extends keyof FallbackType[TComponentName]>(
    key: Key,
    ...args: FuncParams<FallbackType[TComponentName], Key>
  ): Result<FallbackType[TComponentName], Key> {
    const { resolvedTranslation, resolvedLang } = this
    const component = this.host.localName as TComponentName

    // Cast to specific component type to satisfy TS that key is valid for this component
    const componentTranslation = resolvedTranslation[component] as FallbackType[TComponentName] | undefined

    if (componentTranslation == null) {
      console.warn(`NORD: Missing translations for component \`${component}\` in lang: \`${resolvedLang}\``)
    }
    else if (componentTranslation[key] == null) {
      console.warn(
        `NORD: Missing translation key \`${String(key)}\` for component \`${component}\` in lang \`${resolvedLang}\``,
      )
    }

    const fallbackComponentTranslation = fallback[component] as FallbackType[TComponentName]
    const translationValue = (componentTranslation?.[key] ?? fallbackComponentTranslation[key]) as Result<FallbackType[TComponentName], Key>
    return typeof translationValue === 'function' ? translationValue(...args) : translationValue
  }

  private handleLangChange = () => {
    const resolved = resolveTranslation(this.lang)

    if (this.resolvedTranslation !== resolved) {
      this.resolvedTranslation = resolved

      this.options.onLangChange()
      this.host.requestUpdate()
    }
  }
}
